// Code as the editor draws it: coloured words, bracket pairs by depth, rainbow indent
// guides, ligatures. `CodeBlock` is a fenced block (the walkthrough, a reply, a JSON
// attribute); components/DiffFile.tsx lays the same lines out as a diff. The lexer is
// loaded on demand: until it lands, and wherever it is off or the text is too big, the
// code shows as plain text in the same font, so nothing jumps but the colours.
import { useEffect, useMemo, useSyncExternalStore, type CSSProperties } from 'react'
import { syntaxVars } from '@/lib/colour'
import { splitIndent, indentsOf } from '@/lib/syntax/indent'
import { mergeToks } from '@/lib/syntax/brackets'
import type { Line } from '@/lib/syntax/types'
import { useCodeLook } from '@/state/codeLook'
import { cx } from '@/components/ui'

type Highlighter = typeof import('@/lib/syntax')

let loaded: Highlighter | null = null
let pending: Promise<void> | null = null
const waiting = new Set<() => void>()

const load = () => {
  pending ??= import('@/lib/syntax').then((m) => {
    loaded = m
    waiting.forEach((f) => f())
  })
}

/** The lexer, once it has been fetched (null before); asking starts the fetch. */
export const useHighlighter = (wanted = true): Highlighter | null => {
  useEffect(() => {
    if (wanted) load()
  }, [wanted])
  return useSyncExternalStore(
    (f) => {
      waiting.add(f)
      return () => void waiting.delete(f)
    },
    () => loaded,
  )
}

const watchScheme = (f: () => void) => {
  const mo = new MutationObserver(f)
  mo.observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-appearance', 'class'] })
  return () => mo.disconnect()
}
const isLight = () => getComputedStyle(document.documentElement).colorScheme.includes('light')

/** What the colours and markers of one block of code are: its variables and its switches. */
export const useCodeStyle = (): { style: CSSProperties; data: Record<string, string> } => {
  const look = useCodeLook()
  const light = useSyncExternalStore(watchScheme, isLight)
  const style = useMemo(() => syntaxVars(light) as CSSProperties, [light])
  return {
    style,
    data: {
      'data-brackets': look.brackets ? 'on' : 'off',
      'data-guides': look.guides ? 'on' : 'off',
      'data-ligatures': look.ligatures ? 'on' : 'off',
    },
  }
}

/** The leading whitespace of a line drawn as one tinted band per indent level. */
const Indent = ({ text, unit, level }: { text: string; unit: number; level: number }) => {
  const ws = /^[ \t]*/.exec(text)![0]
  if (!ws) {
    // a blank line carries the guides of its neighbours, as empty bands
    return text.trim() === '' && level > 0 ? (
      <>
        {Array.from({ length: level }, (_, i) => (
          <span key={i} className={`ig ig${i % 6} ig-gap`} style={{ width: `${unit}ch` }} aria-hidden />
        ))}
      </>
    ) : null
  }
  const { pieces, rest } = splitIndent(ws, unit)
  return (
    <>
      {pieces.map((p, i) => (
        <span key={i} className={`ig ig${i % 6}`}>
          {p}
        </span>
      ))}
      {rest}
    </>
  )
}

/** One line of code: its guides, then its tokens (merged), or the text as it is when there are none. */
export const CodeLine = ({
  text,
  toks,
  unit,
  level,
  guides = true,
}: {
  text: string
  toks?: Line
  unit: number
  level: number
  guides?: boolean
}) => {
  const indent = guides ? <Indent text={text} unit={unit} level={level} /> : null
  if (!toks) {
    return (
      <>
        {indent}
        {guides ? text.replace(/^[ \t]*/, '') : text}
      </>
    )
  }
  // the tokens after the leading whitespace, which `Indent` has drawn
  const skip = guides ? /^[ \t]*/.exec(text)![0].length : 0
  let left = skip
  const body: Line = []
  for (const t of toks) {
    if (left >= t.s.length) {
      left -= t.s.length
      continue
    }
    body.push(left ? { ...t, s: t.s.slice(left) } : t)
    left = 0
  }
  return (
    <>
      {indent}
      {mergeToks(body).map((t, i) =>
        t.k === 'id' || t.k === 'ws' || (t.k === 'punct' && t.b === undefined) ? (
          t.s
        ) : (
          <span key={i} className={t.b !== undefined ? `tk-b${t.b}` : `tk-${t.k}`}>
            {t.s}
          </span>
        ),
      )}
    </>
  )
}

/** A fenced block of code. `lang` is the fence's word (```ts); an unknown one stays plain. */
export const CodeBlock = ({
  code,
  lang,
  className,
  testId,
}: {
  code: string
  lang?: string
  className?: string
  testId?: string
}) => {
  const on = useCodeLook((s) => s.highlight)
  const guides = useCodeLook((s) => s.guides)
  const hl = useHighlighter(on)
  const { style, data } = useCodeStyle()
  const lines = useMemo(() => code.split('\n'), [code])
  const lx = hl && on ? hl.langOfName(lang) : undefined
  const toks = useMemo(
    () => (hl && lx && !hl.tooBig(lines) ? hl.highlight(lx, lines).lines : undefined),
    [hl, lx, lines],
  )
  const ind = useMemo(() => indentsOf(lines), [lines])
  return (
    <pre
      className={cx(
        'code text-[12.5px] leading-snug rounded-[var(--radius-card-inner)] bg-raised px-3 py-2 my-2 overflow-x-auto whitespace-pre',
        className,
      )}
      style={style}
      data-testid={testId}
      {...data}
    >
      {lines.map((l, i) => (
        <div key={i} className="code-line">
          <CodeLine text={l} toks={toks?.[i]} unit={ind.unit} level={ind.levels[i]!} guides={guides} />
          {'\n'}
        </div>
      ))}
    </pre>
  )
}
